import { useQuery } from '@tanstack/react-query'
import { useCallback, useContext, useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import Loader from '../../../components/global/Loader'
import Title from '../../../components/global/Title'
import RichTextEditor from '../../../components/global/form/RichTextEditor'
import { sweetAlertSuccess } from '../../../components/sweetalert/Alert'
import { StyledButtonLink } from '../../../components/table/Td'
import UserContext from '../../../context/UserContext'
import { useLangSelected } from '../../../hooks/useLangSelected'
import { useVerifyAdminToken } from '../../../hooks/useVerifyAdminToken'
import apiClient from '../../../services/apiClient'
import { FaqCategory } from '../../../types/faq.type'
import { StyledButton } from '../Login'
import { TitleWrapper } from '../coach/AdminCoach'

import styled from '@emotion/styled'

const AdminFaqForm = () => {
    const { t } = useTranslation()
    const user = useContext(UserContext)
    const navigate = useNavigate()
    const params = useParams()
    const verifyToken = useVerifyAdminToken()
    const lng = useLangSelected()

    const [question, setQuestion] = useState('')
    const [answer, setAnswer] = useState('')
    const [category, setCategory] = useState<FaqCategory>(FaqCategory.GENERAL)
    const [order, setOrder] = useState(0)
    const [isActive, setIsActive] = useState(true)
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState('')

    const isEdit = Boolean(params.id)
    const BACK_LINK = `/${lng}/bva-admin/faq`

    useQuery({
        queryKey: ['faq', params.id],
        queryFn: async () => {
            if (!params.id) {
                setIsLoading(false)
                return null
            }

            verifyToken()
            try {
                const response = await apiClient.get(`/faq/${params.id}`)
                const faq = response.data
                setQuestion(faq.question)
                setAnswer(faq.answer)
                setCategory(faq.category)
                setOrder(faq.order)
                setIsActive(faq.isActive)
                return faq
            } catch (error) {
                console.error('Error fetching FAQ:', error)
                setError(t('common.errorOccurred'))
                throw error
            } finally {
                setIsLoading(false)
            }
        },
    })

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault()

            if (!question || !answer) {
                setError(t('common.requiredFields'))
                return
            }

            const data = {
                question,
                answer,
                category,
                order: Number(order) || 0,
                isActive,
            }

            try {
                setIsLoading(true)
                verifyToken()

                if (isEdit && params.id) {
                    await apiClient.patch(`/faq/${params.id}`, data)
                    sweetAlertSuccess(t('faq.updateSuccess'))
                } else {
                    await apiClient.post('/faq', data)
                    sweetAlertSuccess(t('faq.createSuccess'))
                }

                navigate(BACK_LINK)
            } catch (err) {
                console.error('Error saving FAQ:', err)
                setError(t('common.saveError'))
            } finally {
                setIsLoading(false)
            }
        },
        [question, answer, category, order, isActive, navigate, isEdit, params.id, t],
    )

    if (isLoading) return <Loader />

    return (
        <AdminContainer>
            <TitleWrapper>
                <Title>{isEdit ? t('faq.edit') : t('faq.create')}</Title>
                <StyledButtonLink to={BACK_LINK} className="btn btn-secondary">
                    {t('common.back')}
                </StyledButtonLink>
            </TitleWrapper>

            <AdminForm onSubmit={handleSubmit}>
                {error && <div className="alert alert-danger">{error}</div>}
                {error && <div className="text-danger mb-3">{error}</div>}

                <div className="mb-3">
                    <label htmlFor="question" className="form-label">
                        {t('faq.form.question')} *
                    </label>
                    <input
                        type="text"
                        className="form-control"
                        id="question"
                        placeholder={t('faq.form.questionPlaceholder')}
                        value={question}
                        onChange={(e) => setQuestion(e.target.value)}
                        required
                    />
                </div>

                <div className="mb-5">
                    <label htmlFor="answer" className="form-label">
                        {t('faq.form.answer')} *
                    </label>
                    <div className="mb-2">
                        <RichTextEditor
                            value={answer}
                            onChange={setAnswer}
                            placeholder={t('faq.form.answerPlaceholder')}
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col-md-4 mb-3">
                        <label htmlFor="category" className="form-label">
                            {t('faq.form.category')}
                        </label>
                        <select
                            className="form-select"
                            id="category"
                            value={category}
                            onChange={(e) => setCategory(e.target.value as FaqCategory)}
                        >
                            {Object.values(FaqCategory).map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="col-md-4 mb-3">
                        <label htmlFor="order" className="form-label">
                            {t('faq.form.order')}
                        </label>
                        <input
                            type="number"
                            className="form-control"
                            id="order"
                            value={order}
                            onChange={(e) => setOrder(Number(e.target.value))}
                            min="0"
                        />
                    </div>

                    <div className="col-md-4 mb-3 d-flex align-items-end">
                        <div className="form-check form-switch">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="isActive"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="isActive">
                                {t('common.active')}
                            </label>
                        </div>
                    </div>
                </div>

                <div className="d-flex justify-content-between mt-4">
                    <StyledButton type="button" variant="secondary" onClick={() => navigate(BACK_LINK)}>
                        {t('common.cancel')}
                    </StyledButton>
                    <StyledButton type="submit" variant="primary">
                        {isEdit ? t('common.save') : t('common.create')}
                    </StyledButton>
                </div>
            </AdminForm>
        </AdminContainer>
    )
}

export const AdminContainer = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
`

export const AdminForm = styled(Form)`
    background: white;
    padding: 2rem;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`

export default AdminFaqForm
